import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Container,
  Grid,
  Paper,
  Button,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ScienceIcon from '@mui/icons-material/Science';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LogoutIcon from '@mui/icons-material/Logout';
import HelpIcon from '@mui/icons-material/Help';
import CodeIcon from '@mui/icons-material/Code';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HelpDialog from '../HelpDialog/HelpDialog';
import { exportToExcel, exportAnalysisReport, generateAnalysisText, exportCompleteRunToExcel } from '../../utils/exportUtils';
import { useOptimization } from '../../context/OptimizationContext';
import { OptimizationParameters } from '../../types';
import { NotificationSnackbar } from '../common/NotificationSnackbar';
import ParameterPanel from '../ParameterPanel/ParameterPanel';
import DatasetUpload from '../DatasetUpload/DatasetUpload';
import OptimizationControl from '../OptimizationControl/OptimizationControl';
import OptimizationProgress from '../OptimizationProgress/OptimizationProgress';
import ResultsView from '../ResultsView/ResultsView';
import ChartsView from '../ChartsView/ChartsView';
import HistoryView from '../HistoryView/HistoryView';
import EnergyLeaderboard from '../EnergyLeaderboard/EnergyLeaderboard';
import CombinedWinnerLeaderboard from '../CombinedWinnerLeaderboard/CombinedWinnerLeaderboard';
import Footer from './Footer';
import CompleteRunModal from '../CompleteRunModal/CompleteRunModal';
import AlgorithmsModal from '../AlgorithmsModal/AlgorithmsModal';
import settings from '../../config/settings';

const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isRunning, startOptimization, startCompleteRun, cancelOptimization, results, notification, showNotification, hideNotification } = useOptimization();
  const parametersRef = useRef<OptimizationParameters | null>(null);
  const datasetRef = useRef<string>('');
  const [currentParameters, setCurrentParameters] = useState<OptimizationParameters | null>(null);
  const [completeRunModalOpen, setCompleteRunModalOpen] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [algorithmsModalOpen, setAlgorithmsModalOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const menuOpen = Boolean(menuAnchorEl);
  
  const showAlgorithms = settings.showAlgorithms === 1;

  const handleLogout = () => {
    sessionStorage.removeItem('isAuthenticated');
    sessionStorage.removeItem('username');
    navigate('/');
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleDownloadDataset = () => {
    try {
      // Create a link element and trigger download
      const link = document.createElement('a');
      link.href = '/QWS_Dataset.txt';
      link.download = 'QWS_Dataset.txt';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showNotification('QWS Dataset downloaded successfully', 'success');
    } catch (error) {
      console.error('Error downloading dataset:', error);
      showNotification('Failed to download dataset', 'error');
    }
  };

  const handleParametersChange = (params: {
    taskCount: number;
    qosProperties: number;
    iterations: number;
    initialPopulation: number;
    selectionRate: number;
    crossoverRate: number;
    mutationRate: number;
    memeplexCount: number;
    numberOfRuns?: number;
    fitnessMode?: 'qos-only' | 'energy-aware';
    energyAwareWeights?: { fitness: number; energy: number; cost: number };
  }) => {
    const optParams: OptimizationParameters = {
      taskCount: params.taskCount,
      qosProperties: params.qosProperties,
      iterations: params.iterations,
      initialPopulation: params.initialPopulation,
      selectionRate: params.selectionRate,
      crossoverRate: params.crossoverRate,
      mutationRate: params.mutationRate,
      memeplexCount: params.memeplexCount,
      numberOfRuns: params.numberOfRuns || 1,
      fitnessMode: params.fitnessMode,
      energyAwareWeights: params.energyAwareWeights,
    };
    parametersRef.current = optParams;
    setCurrentParameters(optParams);
  };

  const handleLoadPreset = (parameters: OptimizationParameters) => {
    setCurrentParameters(parameters);
    parametersRef.current = parameters;
    showNotification('Preset loaded successfully', 'success');
  };

  const handleFileSelect = (file: File, content: string) => {
    datasetRef.current = content;
  };

  const handleStart = async () => {
    const currentParams = parametersRef.current;
    const currentDataset = datasetRef.current;

    if (!currentParams) {
      showNotification('Please configure parameters first', 'warning');
      return;
    }

    if (!currentDataset) {
      showNotification('Please select a dataset file first', 'warning');
      return;
    }

    await startOptimization(currentParams, currentDataset);
  };

  const handleExportExcel = () => {
    if (!results) {
      showNotification('No results available to export', 'warning');
      return;
    }
    
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `optimization_results_${timestamp}.xlsx`;
    exportToExcel(results, filename);
    showNotification('Excel file downloaded successfully', 'success');
  };

  const handleExportAnalysis = () => {
    if (!results) {
      showNotification('No results available to export', 'warning');
      return;
    }
    
    const analysisText = generateAnalysisText(results);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `analysis_report_${timestamp}.txt`;
    exportAnalysisReport(analysisText, filename);
    showNotification('Analysis report downloaded successfully', 'success');
  };

  const handleCompleteRun = async (workflowStart: number, workflowEnd: number, increment: number) => {
    const currentParams = parametersRef.current;
    const currentDataset = datasetRef.current;

    if (!currentParams) {
      showNotification('Please configure parameters first', 'warning');
      return;
    }

    if (!currentDataset) {
      showNotification('Please select a dataset file first', 'warning');
      return;
    }

    try {
      const completeRunResults = await startCompleteRun(
        currentParams,
        currentDataset,
        workflowStart,
        workflowEnd,
        increment,
        (current, total, taskCount, params) => {
          // Update UI parameters to show current task count and auto-adjusted parameters
          if (params) {
            setCurrentParameters(params);
            parametersRef.current = params;
          }
          showNotification(
            `Complete Run: ${current}/${total} completed (${taskCount} tasks)`,
            'info'
          );
        }
      );

      // Export results to Excel
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `complete_run_${workflowStart}_to_${workflowEnd}_${timestamp}.xlsx`;
      exportCompleteRunToExcel(completeRunResults, filename);
      showNotification(
        `Complete run finished! ${completeRunResults.length} optimizations completed. Results exported to Excel.`,
        'success'
      );
    } catch (error) {
      console.error('Complete run error:', error);
      showNotification(
        'Complete run failed: ' + (error instanceof Error ? error.message : String(error)),
        'error'
      );
    }
  };

  const hasResults = results !== null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Header */}
      <AppBar position="static" elevation={2}>
        <Toolbar sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' }, gap: { xs: 1, md: 0 } }}>
          <ScienceIcon sx={{ mr: { xs: 1, md: 2 } }} />
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              display: { xs: 'none', sm: 'block' }
            }}
          >
            Service Composition Optimizer
          </Typography>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontSize: '1rem',
              display: { xs: 'block', sm: 'none' }
            }}
          >
            Optimizer
          </Typography>
          
          {/* Mobile Menu */}
          {isMobile ? (
            <>
              <IconButton
                color="inherit"
                onClick={handleMenuOpen}
                sx={{ ml: 'auto' }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={menuAnchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: { minWidth: 200 }
                }}
              >
                <MenuItem onClick={() => { handleDownloadDataset(); handleMenuClose(); }}>
                  <FileDownloadIcon sx={{ mr: 1 }} />
                  Download Dataset
                </MenuItem>
                <MenuItem onClick={() => { setHelpDialogOpen(true); handleMenuClose(); }}>
                  <HelpIcon sx={{ mr: 1 }} />
                  Help
                </MenuItem>
                {showAlgorithms && (
                  <MenuItem onClick={() => { setAlgorithmsModalOpen(true); handleMenuClose(); }}>
                    <CodeIcon sx={{ mr: 1 }} />
                    Algorithms
                  </MenuItem>
                )}
                <MenuItem onClick={() => { handleExportExcel(); handleMenuClose(); }} disabled={!hasResults}>
                  <FileDownloadIcon sx={{ mr: 1 }} />
                  Export Excel
                </MenuItem>
                <MenuItem onClick={() => { handleExportAnalysis(); handleMenuClose(); }} disabled={!hasResults}>
                  <AssessmentIcon sx={{ mr: 1 }} />
                  Export Analysis
                </MenuItem>
                <MenuItem onClick={() => { handleLogout(); handleMenuClose(); }}>
                  <LogoutIcon sx={{ mr: 1 }} />
                  Logout
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                startIcon={<FileDownloadIcon />}
                onClick={handleDownloadDataset}
                sx={{
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                Download Dataset
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                startIcon={<HelpIcon />}
                onClick={() => setHelpDialogOpen(true)}
                sx={{
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                Help
              </Button>
              {showAlgorithms && (
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  startIcon={<CodeIcon />}
                  onClick={() => setAlgorithmsModalOpen(true)}
                  sx={{
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  Algorithms
                </Button>
              )}
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                sx={{ 
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                Logout
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportExcel}
                disabled={!hasResults}
                sx={{ 
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                  '&.Mui-disabled': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                Export Excel
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                startIcon={<AssessmentIcon />}
                onClick={handleExportAnalysis}
                disabled={!hasResults}
                sx={{ 
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                  '&.Mui-disabled': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                Export Analysis
              </Button>
            </Box>
          )}
          
          {/* Action Buttons */}
          {!isRunning ? (
            <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', md: 'auto' }, mt: { xs: 1, md: 0 } }}>
              <Button
                variant="contained"
                color="secondary"
                size={isMobile ? 'medium' : 'large'}
                startIcon={<PlayArrowIcon />}
                onClick={handleStart}
                sx={{ 
                  minWidth: { xs: 'auto', md: 180 },
                  flex: { xs: 1, md: 'none' }
                }}
              >
                {isMobile ? 'Start' : 'Start Optimization'}
              </Button>
              <Button
                variant="contained"
                color="primary"
                size={isMobile ? 'medium' : 'large'}
                startIcon={<AutoAwesomeIcon />}
                onClick={() => setCompleteRunModalOpen(true)}
                sx={{ 
                  minWidth: { xs: 'auto', md: 180 },
                  flex: { xs: 1, md: 'none' }
                }}
              >
                {isMobile ? 'Complete' : 'Complete Run'}
              </Button>
            </Box>
          ) : (
            <Button
              variant="contained"
              color="error"
              size={isMobile ? 'medium' : 'large'}
              startIcon={<StopIcon />}
              onClick={cancelOptimization}
              sx={{ 
                minWidth: { xs: 'auto', md: 180 },
                width: { xs: '100%', md: 'auto' },
                mt: { xs: 1, md: 0 }
              }}
            >
              {isMobile ? 'Cancel' : 'Cancel Optimization'}
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container 
        maxWidth="xl" 
        sx={{ 
          mt: { xs: 2, md: 3 }, 
          mb: { xs: 2, md: 3 }, 
          flexGrow: 1, 
          px: { xs: 1, sm: 2, md: 2, lg: 0 } 
        }}
      >
        <Box sx={{ position: 'relative' }}>
          {/* Toggle Button - Desktop */}
          {sidebarOpen ? (
            <IconButton
              onClick={() => setSidebarOpen(false)}
              sx={{
                position: 'absolute',
                left: { md: -20 },
                top: { md: 20 },
                zIndex: 1000,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 2,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                display: { xs: 'none', md: 'flex' },
              }}
              size="small"
              aria-label="Hide sidebar"
            >
              <ChevronLeftIcon />
            </IconButton>
          ) : (
            <IconButton
              onClick={() => setSidebarOpen(true)}
              sx={{
                position: 'absolute',
                left: { md: 0 },
                top: { md: 20 },
                zIndex: 1000,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderLeft: 'none',
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                boxShadow: 2,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                display: { xs: 'none', md: 'flex' },
              }}
              size="small"
              aria-label="Show sidebar"
            >
              <ChevronRightIcon />
            </IconButton>
          )}

          <Grid container spacing={{ xs: 1.5, md: 2 }}>
            {/* Left Sidebar - Parameters and Controls */}
            {sidebarOpen && (
              <Grid 
                item 
                xs={12} 
                md={4} 
                lg={3} 
                sx={{ 
                  position: { xs: 'static', md: 'sticky' }, 
                  top: { md: 24 }, 
                  alignSelf: 'flex-start' 
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 } }}>
                  {/* Toggle Button for Mobile */}
                  <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'flex-end', mb: -1 }}>
                    <IconButton
                      onClick={() => setSidebarOpen(false)}
                      size="small"
                      sx={{ color: 'text.secondary' }}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                  </Box>

                  {/* Parameter Configuration */}
                  <Paper elevation={2} sx={{ p: { xs: 2, md: 3 } }}>
                    <ParameterPanel
                      initialValues={currentParameters ? {
                        taskCount: currentParameters.taskCount,
                        qosProperties: currentParameters.qosProperties,
                        iterations: currentParameters.iterations,
                        initialPopulation: currentParameters.initialPopulation,
                        selectionRate: currentParameters.selectionRate,
                        crossoverRate: currentParameters.crossoverRate,
                        mutationRate: currentParameters.mutationRate,
                        memeplexCount: currentParameters.memeplexCount,
                        numberOfRuns: currentParameters.numberOfRuns,
                        fitnessMode: currentParameters.fitnessMode,
                        energyAwareWeights: currentParameters.energyAwareWeights,
                      } : undefined}
                      onParametersChange={handleParametersChange}
                    />
                  </Paper>

                  {/* Dataset Upload */}
                  <Paper elevation={2} sx={{ p: { xs: 2, md: 3 } }}>
                    <DatasetUpload onFileSelect={handleFileSelect} onError={(msg) => showNotification(msg, 'error')} />
                  </Paper>

                  {/* Optimization Control */}
                  <Paper elevation={2} sx={{ p: { xs: 2, md: 3 } }}>
                    <OptimizationControl />
                  </Paper>
                </Box>
              </Grid>
            )}

            {/* Show Toggle Button when Sidebar is Hidden (Mobile) */}
            {!sidebarOpen && (
              <Box sx={{ display: { xs: 'block', md: 'none' }, width: '100%', mb: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<ChevronRightIcon />}
                  onClick={() => setSidebarOpen(true)}
                  fullWidth
                  size="small"
                >
                  Show Parameters Panel
                </Button>
              </Box>
            )}

            {/* Main Content Area - Results and Charts */}
            <Grid item xs={12} md={sidebarOpen ? 8 : 12} lg={sidebarOpen ? 7 : 9}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 } }}>
              {/* Optimization Progress */}
              <OptimizationProgress />

              {/* Results Tabs */}
              <Paper elevation={2} sx={{ p: { xs: 1.5, md: 2 } }}>
                <ResultsView />
              </Paper>

              {/* Charts */}
              <Paper elevation={2} sx={{ p: { xs: 1.5, md: 2 } }}>
                <ChartsView />
              </Paper>

              {/* History */}
              <Paper elevation={2} sx={{ p: { xs: 1.5, md: 2 } }}>
                <HistoryView />
              </Paper>
            </Box>
          </Grid>

          {/* Right Sidebar - Energy Leaderboard and Combined Winner */}
          <Grid item xs={12} md={12} lg={sidebarOpen ? 2 : 3}>
            <Box
              sx={{
                position: { xs: 'static', md: 'sticky' },
                top: { md: 24 },
                alignSelf: 'flex-start',
              }}
            >
              <EnergyLeaderboard />
              <CombinedWinnerLeaderboard />
            </Box>
          </Grid>
        </Grid>
        </Box>
      </Container>

      {/* Footer */}
      <Footer />

      {/* Notification Snackbar */}
      {notification && (
        <NotificationSnackbar
          open={notification.open}
          message={notification.message}
          severity={notification.severity}
          onClose={hideNotification}
        />
      )}

      {/* Complete Run Modal */}
      <CompleteRunModal
        open={completeRunModalOpen}
        onClose={() => setCompleteRunModalOpen(false)}
        onSubmit={handleCompleteRun}
        defaultStart={currentParameters?.taskCount || 10}
        defaultEnd={1000}
        defaultIncrement={10}
      />

      {/* Help Dialog */}
      <HelpDialog
        open={helpDialogOpen}
        onClose={() => setHelpDialogOpen(false)}
      />

      {/* Algorithms Modal */}
      <AlgorithmsModal
        open={algorithmsModalOpen}
        onClose={() => setAlgorithmsModalOpen(false)}
      />
    </Box>
  );
};

export default MainLayout;

